<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-wrap gap-1">
      <Chip
        v-for="preset in presets"
        :key="preset.key"
        :selected="selectedPreset === preset.key"
        class="chip-preset"
        @click="selectPreset(preset.key)"
      >
        {{ preset.label }}
      </Chip>
    </div>
    <FilterItemDate :selected-dates="dates" @updated="onPickerUpdated" />
  </div>
</template>

<script setup lang="ts">
defineOptions({ inheritAttrs: false })

const { dates = [] } = defineProps<{ dates?: string[] }>()
const emit = defineEmits(['updated', 'confirmed'])
const { t } = useI18n()

const formatDate = (d: Date): string => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const getTodayRange = (): [string, string] => {
  const today = formatDate(new Date())
  return [today, today]
}

const getWeekendRange = (): [string, string] => {
  const now = new Date()
  const day = now.getDay()
  let sat: Date, sun: Date
  if (day === 6) {
    sat = new Date(now)
    sun = new Date(now)
    sun.setDate(sun.getDate() + 1)
  } else if (day === 0) {
    // Sunday — Saturday is already in the past, so only offer the rest of today
    sat = new Date(now)
    sun = new Date(now)
  } else {
    sat = new Date(now)
    sat.setDate(sat.getDate() + (6 - day))
    sun = new Date(sat)
    sun.setDate(sun.getDate() + 1)
  }
  return [formatDate(sat), formatDate(sun)]
}

const getNext7DaysRange = (): [string, string] => {
  const start = new Date()
  const end = new Date()
  end.setDate(end.getDate() + 6)
  return [formatDate(start), formatDate(end)]
}

const presets = computed(() => [
  {
    key: 'today' as const,
    label: t('search.filters.dates.today'),
    range: getTodayRange(),
  },
  {
    key: 'weekend' as const,
    label: t('search.filters.dates.thisWeekend'),
    range: getWeekendRange(),
  },
  {
    key: 'next7' as const,
    label: t('search.filters.dates.next7Days'),
    range: getNext7DaysRange(),
  },
])

type PresetKey = 'today' | 'weekend' | 'next7' | null

function detectPreset(): PresetKey {
  if (!dates?.length) return null
  for (const p of presets.value) {
    if (dates[0] === p.range[0] && dates[1] === p.range[1]) return p.key
  }
  return null
}

const selectedPreset = ref<PresetKey>(detectPreset())

function selectPreset(key: PresetKey) {
  if (selectedPreset.value === key) {
    selectedPreset.value = null
    emit('updated', [])
    emit('confirmed')
    return
  }
  selectedPreset.value = key
  const preset = presets.value.find((p) => p.key === key)
  if (preset) {
    emit('updated', preset.range)
    emit('confirmed')
  }
}

function onPickerUpdated(range: string[]) {
  selectedPreset.value = null
  emit('updated', range)
}
</script>

<style lang="postcss" scoped>
.chip-preset {
  @apply m-0;

  &:not(.chip--selected) {
    @apply hover:border-teal-900 hover:bg-white hover:text-gray-700;
  }
}
</style>
